import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import notify from '../../../utils/notify';

import ProposalStripePayment from './ProposalStripePayment';
import AddFundsModal from '../../Credits/ApplyFundsModal';
import { CreditCard, Receipt, PlusCircle, X, Banknote } from 'lucide-react';

import '../../../component-styles/TimeBillingInvoiceView.css';
import API_BASE from '../../../utils/api.js';
import { roundCurrency, sanitizeCurrencyInput } from '../../../utils/currency.js';
import { getProposalInvoicingStatus } from '../../../utils/proposalInvoicing.js';
import usePermissions from '../../../auth/usePermissions';

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

const MANUAL_METHOD_OPTIONS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'ach', label: 'ACH' },
  { value: 'wire_transfer', label: 'Wire Transfer' },
  { value: 'other', label: 'Other' },
];

const emptyManualForm = () => ({
  amount: '',
  method: 'bank_transfer',
  paymentDate: new Date().toISOString().slice(0, 10),
  reference: '',
  recordedInQuickBooks: false,
});

// prefillManualPayment ({ amount }) opens the manual payment form from outside,
// e.g. for the deposit Convert to PO is waiting on; onManualPaymentClosed(recorded)
// reports back when that form closes.
export default function ProposalPayments({
  proposal,
  projectId,
  onUpdated,
  quickbooksConnected = false,
  prefillManualPayment = null,
  onManualPaymentClosed,
}) {
  const { permissions } = usePermissions();
  const canApplyPayments = permissions.applyInvoiceProposalPayments !== false;

  const userId = localStorage.getItem('userId') || proposal?.userId || '';
  const token = localStorage.getItem('idToken');

  const total = roundCurrency(
    proposal?.totals?.grandTotal ??
      proposal?.totals?.total ??
      Number(proposal?.totals?.subtotal || 0) + Number(proposal?.totals?.tax || 0)
  );
  const paid = roundCurrency(proposal?.totals?.paid || 0);
  // Once every item is invoiced, the client pays on the invoice. That includes
  // older proposals that were converted whole and have a stale stored balance.
  const remainingBalance =
    getProposalInvoicingStatus(proposal) === 'invoiced'
      ? 0
      : Number.isFinite(Number(proposal?.remainingBalance))
        ? roundCurrency(proposal.remainingBalance)
        : roundCurrency(Math.max(total - paid, 0));

  const [showChoice, setShowChoice] = useState(false);
  const [showStripe, setShowStripe] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showCredits, setShowCredits] = useState(false);
  const [expandedPaymentId, setExpandedPaymentId] = useState(null);

  const payments = useMemo(() => proposal?.paymentHistory || [], [proposal]);
  const [manualForm, setManualForm] = useState(emptyManualForm);

  useEffect(() => {
    if (!prefillManualPayment || !canApplyPayments) return;
    const amount = Number(prefillManualPayment.amount);
    setManualForm({
      ...emptyManualForm(),
      amount: Number.isFinite(amount) && amount > 0 ? amount.toFixed(2) : '',
    });
    setShowManual(true);
  }, [prefillManualPayment, canApplyPayments]);

  function closeManual() {
    setShowManual(false);
    onManualPaymentClosed?.(false);
  }

  async function applyManualPayment() {
    const amt = roundCurrency(manualForm.amount);
    if (!amt || amt <= 0) return;

    const res = await fetch(
      `${(API_BASE).replace(
        /\/$/,
        ''
      )}/api/proposals/${projectId}/${proposal.proposalId}/apply-payment`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          userid: userId,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          userId,
          amount: amt,
          paymentType: 'manual',
          method: manualForm.method,
          paymentDate: manualForm.paymentDate,
          reference: manualForm.reference,
          recordedInQuickBooks: quickbooksConnected && manualForm.recordedInQuickBooks,
        }),
      }
    );

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      notify(data.error || 'Failed to record payment');
      return;
    }

    setShowManual(false);
    setManualForm(emptyManualForm());
    onUpdated?.();
    onManualPaymentClosed?.(true);
  }

  return (
    <div className="rd-payments-section">
      <h3>Payments</h3>

      {payments.length === 0 && <div className="rd-empty">No payments yet.</div>}

      {payments.length > 0 && (
        <table className="rd-payments-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Method</th>
              <th>Reference</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => {
              const isExpanded = expandedPaymentId === p.paymentId;
              const paidToDate = payments
                .slice(0, payments.findIndex((x) => x.paymentId === p.paymentId) + 1)
                .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
              const remainingAfter = Math.max(total - paidToDate, 0);
              return (
                <React.Fragment key={p.paymentId}>
                  <tr
                    className="rd-payment-row"
                    onClick={() => setExpandedPaymentId(isExpanded ? null : p.paymentId)}
                  >
                    <td>{p.date?.slice(0, 10)}</td>
                    <td>{p.method || p.paymentType}</td>
                    <td className="rd-ref-cell">{p.reference || '-'}</td>
                    <td>{formatCurrency(p.amount)}</td>
                  </tr>
                  {isExpanded && (
                    <tr className="rd-payment-detail-row">
                      <td colSpan={4}>
                        <div className="rd-payment-details">
                          <span>Payment ID: {p.paymentId || '-'}</span>
                          <span>Recorded at: {p.date ? new Date(p.date).toLocaleString() : '-'}</span>
                          <span>Type: {p.paymentType || '-'}</span>
                          <span>Credit ID: {p.creditId || '-'}</span>
                          <span>Retainer ID: {p.retainerId || '-'}</span>
                          <span>Running balance: {formatCurrency(remainingAfter)}</span>
                          {p.recordedInQuickBooks && (
                            <span>Paid in QuickBooks — not posted there from InteriorFolio</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {remainingBalance > 0 && canApplyPayments && (
        <button className="rd-pay-btn no-pdf" onClick={() => setShowChoice(true)}>
          <PlusCircle size={16} style={{ marginRight: 6 }} />
          Make a Payment
        </button>
      )}

      {canApplyPayments && showChoice && (
        <div className="rd-overlay payment-popup-overlay" onClick={() => setShowChoice(false)}>
          <div className="rd-choice-modal payment-popup-panel" onClick={(e) => e.stopPropagation()}>
            <button className="rd-choice-close" onClick={() => setShowChoice(false)}>
              <X size={18} />
            </button>

            <h3>Select Payment Method</h3>

            <div className="rd-choice-buttons">
              <button
                className="rd-choice-btn rd-stripe"
                onClick={() => {
                  setShowChoice(false);
                  setShowStripe(true);
                }}
              >
                <CreditCard size={18} /> Pay with Stripe
              </button>

              <button
                className="rd-choice-btn rd-manual"
                onClick={() => {
                  setShowChoice(false);
                  setShowManual(true);
                }}
              >
                <Banknote size={18} /> Record Manual Payment
              </button>

              <button
                className="rd-choice-btn rd-credit"
                onClick={() => {
                  setShowChoice(false);
                  setShowCredits(true);
                }}
              >
                <Receipt size={18} /> Apply Retainer/Credit
              </button>
            </div>
          </div>
        </div>
      )}

      {canApplyPayments && showStripe && (
        <ProposalStripePayment
          projectId={projectId}
          proposalId={proposal.proposalId}
          proposalNumber={proposal.proposalNumber}
          remainingBalance={remainingBalance}
          onSuccess={() => {
            setShowStripe(false);
            onUpdated?.();
          }}
          onClose={() => setShowStripe(false)}
        />
      )}

      {canApplyPayments && showManual && (
        <div className="rd-overlay payment-popup-overlay" onClick={closeManual}>
          <div className="rd-modal payment-popup-panel" onClick={(e) => e.stopPropagation()}>
            <h3>Record Manual Payment</h3>

            <label>Amount</label>
            <input
              type="number"
              step="0.01"
              value={manualForm.amount}
              onChange={(e) =>
                setManualForm({ ...manualForm, amount: sanitizeCurrencyInput(e.target.value) })
              }
            />

            <label>Method</label>
            <select
              value={manualForm.method}
              onChange={(e) => setManualForm({ ...manualForm, method: e.target.value })}
            >
              {MANUAL_METHOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <label>Payment Date</label>
            <input
              type="date"
              value={manualForm.paymentDate}
              onChange={(e) => setManualForm({ ...manualForm, paymentDate: e.target.value })}
            />

            <label>Reference</label>
            <input
              type="text"
              value={manualForm.reference}
              onChange={(e) => setManualForm({ ...manualForm, reference: e.target.value })}
            />

            {quickbooksConnected && (
              <>
                <label className="rd-check-row">
                  <input
                    type="checkbox"
                    checked={manualForm.recordedInQuickBooks}
                    onChange={(e) =>
                      setManualForm({ ...manualForm, recordedInQuickBooks: e.target.checked })
                    }
                  />
                  Already in QuickBooks
                </label>
                <p className="rd-check-hint">
                  The client paid you in QuickBooks. It&apos;s recorded here but not posted to
                  QuickBooks again, including when this proposal is invoiced.
                </p>
              </>
            )}

            <div className="rd-modal-actions">
              <button className="rd-cancel" onClick={closeManual}>
                Cancel
              </button>
              <button className="rd-save" onClick={applyManualPayment}>
                Save Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {canApplyPayments && showCredits && (
        <AddFundsModal
          projectId={projectId}
          targetId={proposal.proposalId}
          targetType="proposal"
          targetRemaining={remainingBalance}
          onApplied={() => {
            setShowCredits(false);
            onUpdated?.();
          }}
          onClose={() => setShowCredits(false)}
        />
      )}
    </div>
  );
}

ProposalPayments.propTypes = {
  proposal: PropTypes.object.isRequired,
  projectId: PropTypes.string.isRequired,
  onUpdated: PropTypes.func,
  quickbooksConnected: PropTypes.bool,
  prefillManualPayment: PropTypes.shape({ amount: PropTypes.number }),
  onManualPaymentClosed: PropTypes.func,
};
